import { AiConverseDto } from '../generated-types/aiConverseDto';
import { AbstractApi } from './AbstractApi';

/**
 * AiApi class handles AI-related API interactions, such as fetching initial messages and conversing with the AI.
 * It extends the AbstractApi class, inheriting common API functionalities.
 */
export default class AiApi extends AbstractApi {
  /**
   * Retrieves the initial AI message intended for managers.
   *
   * @async
   * @returns {Promise<string>} A promise that resolves to the initial AI message as a string.
   * @throws Will throw an error if the response is not OK.
   */
  async GetInitialManagerResponse(): Promise<string> {
    const response = await this.authFetch(
      this.BuildUrl('ai/converse/manager/initial-message'),
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) throw Error();

    return await response.text();
  }

  /**
   * Sends a conversation message to the AI and retrieves the AI's response.
   *
   * @async
   * @param {AiConverseDto} message - The conversation message DTO containing the manager's message.
   * @returns {Promise<AiConverseDto>} A promise that resolves to the AI's response as an AiConverseDto object.
   * @throws Will throw an error if the response is not OK.
   */
  async Converse(message: AiConverseDto): Promise<AiConverseDto> {
    const response = await this.authFetch(
      this.BuildUrl('ai/converse/manager'),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(message),
      }
    );

    if (!response.ok) throw Error();

    return (await response.json()) as AiConverseDto;
  }
}
